<template>
  <main-template>
    <v-row class="mb-4">
      <v-col cols="12">
        <h1 class="d-flex align-center font-weight-black primary--text">
          <v-btn
            color="primary"
            icon
            large
            class="mr-2"
            @click="() => $router.push({ name: 'usuarios' })"
            :disabled="submittingForm"
          >
            <v-icon left> fa fa-angle-left </v-icon>
          </v-btn>
          Recursos Humanos
        </h1>
      </v-col>
    </v-row>

    <ValidationObserver v-slot="{ handleSubmit }">
      <form
        @submit.prevent="handleSubmit(submitForm)"
        autocomplete="off"
        :disabled="submittingForm"
      >
        <v-card class="mb-6" outlined rounded="xl">
          <v-card-title class="px-6 pt-6 pb-2">
            <v-icon left color="primary">mdi-account</v-icon>
            <span class="text-h6 font-weight-bold primary--text">Informações Pessoais</span>
          </v-card-title>
          <v-card-text class="px-6 pb-6">
            <v-row>
              <v-col cols="12" class="d-flex align-center py-4">
                <v-avatar
                  size="100"
                  class="mr-6"
                  color="green lighten-5"
                  style="
                    border: 3px solid var(--brand);
                    box-shadow: 0 4px 12px rgba(79, 0, 143, 0.15);
                  "
                >
                  <v-img v-if="fotoUrl" :src="fotoUrl" />
                  <span v-else class="text-h3 font-weight-bold primary--text">
                    {{ ((form && form.pessoal && form.pessoal.name) || "...").substr(0, 1) }}
                  </span>
                </v-avatar>
                <div v-if="isAdminOrSuperAdmin">
                  <v-btn
                    color="primary"
                    outlined
                    small
                    @click="triggerFotoInput"
                    :loading="uploadingFoto"
                    class="font-weight-bold rounded-lg"
                  >
                    <v-icon left>mdi-camera</v-icon>
                    Alterar Foto
                  </v-btn>
                  <input
                    id="fotoUpload"
                    type="file"
                    accept="image/*"
                    @change="onFotoSelected"
                    style="display: none"
                  />
                  <div class="text-caption grey--text mt-1">
                    Apenas arquivos de imagem (JPEG, PNG). Máx: 2MB.
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="4" class="pt-0 pb-0">
                <ValidationProvider name="Nome Completo" rules="required" v-slot="{ errors }">
                  <e-label>Nome Completo</e-label>
                  <v-text-field
                    :error-messages="errors"
                    v-model="form.pessoal.name"
                    dense
                    solo
                    disabled
                  />
                </ValidationProvider>
              </v-col>

              <v-col cols="12" md="4" class="pt-0 pb-0">
                <e-label>Estado Civil</e-label>
                <v-autocomplete
                  :items="$constants.estadosCivisList"
                  :return-object="false"
                  item-value="id"
                  item-text="descricao"
                  v-model="form.pessoal.estado_civil_id"
                  label="Selecione uma opção"
                  dense
                  solo
                />
              </v-col>

              <v-col cols="12" md="4" class="pt-0 pb-0">
                <e-label>Sexo</e-label>
                <v-autocomplete
                  :items="$constants.sexoList"
                  :return-object="false"
                  item-value="id"
                  item-text="descricao"
                  v-model="form.pessoal.sexo_id"
                  label="Selecione uma opção"
                  dense
                  solo
                />
              </v-col>

              <v-col cols="12" md="4" class="pt-0 pb-0">
                <e-label>Cor/Raça</e-label>
                <v-autocomplete
                  :items="$constants.corOuRacaList"
                  :return-object="false"
                  v-model="form.pessoal.cor_ou_raca"
                  label="Selecione uma opção"
                  dense
                  solo
                />
              </v-col>

              <v-col cols="12" md="4" class="pt-0 pb-0">
                <e-label>Data de nascimento</e-label>
                <e-date-customize dense solo v-model="form.pessoal.data_nascimento" />
              </v-col>

              <v-col cols="12" md="4" class="pt-0 pb-0">
                <e-label>Líder Imediato</e-label>
                <v-autocomplete
                  :items="lideresList"
                  :return-object="false"
                  item-value="id"
                  item-text="name"
                  v-model="form.pessoal.lider_id"
                  label="Selecione o Líder"
                  dense
                  solo
                  clearable
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="mb-6" outlined rounded="xl">
          <v-card-title class="px-6 pt-6 pb-2 d-flex justify-space-between align-center">
            <div>
              <v-icon left color="primary">mdi-briefcase</v-icon>
              <span class="text-h6 font-weight-bold primary--text">Contratos e Vínculos</span>
            </div>
            <v-btn
              color="primary"
              outlined
              small
              @click="() => handleSubmit(adicionarRelacaoProfissional)"
              :disabled="submittingForm || !podeAdicionarMaisProfissionais"
              class="rounded-lg"
            >
              <v-icon left>mdi-plus</v-icon> Adicionar Contrato
            </v-btn>
          </v-card-title>
          <v-card-text class="px-6 pb-6">
            <div
              v-if="!form.profissionais || form.profissionais.length === 0"
              class="text-center py-6 grey--text font-italic"
            >
              Nenhum vínculo profissional cadastrado. Clique no botão acima para adicionar.
            </div>

            <v-card
              v-for="(profissional, index) in form.profissionais"
              :key="'prof-' + index"
              class="mb-4 pa-4"
              outlined
              rounded="lg"
              style="background-color: var(--surface-2); border: 1px dashed var(--border)"
            >
              <div class="d-flex justify-space-between align-center mb-3">
                <span class="text-subtitle-1 font-weight-bold primary--text"
                  >Vínculo/Contrato #{{ index + 1 }}</span
                >
                <v-btn
                  color="error"
                  text
                  small
                  @click="() => removerRelacaoProfissional(index)"
                  :disabled="submittingForm"
                >
                  <v-icon left>mdi-delete-outline</v-icon> Remover Contrato
                </v-btn>
              </div>

              <v-row>
                <v-col cols="12" md="8" class="pt-0 pb-0">
                  <e-label style="font-weight: bold">Matrícula / ID Funcional</e-label>
                  <v-text-field
                    v-model="profissional.matricula"
                    dense
                    solo
                    v-mask="'#######################'"
                  />
                </v-col>

                <v-col cols="12" md="4" class="pt-0 pb-0">
                  <e-label>Regime de Contratação</e-label>
                  <v-autocomplete
                    :items="$constants.regimeTrabalhistaList"
                    :return-object="false"
                    v-model="profissional.regime_trabalhista"
                    label="Selecione uma opção"
                    dense
                    solo
                  />
                </v-col>

                <v-col cols="12" md="4" class="pt-0 pb-0">
                  <div
                    class="text-caption primary--text font-weight-medium mt-1"
                    style="cursor: pointer"
                    @click="abrirDialogNovoCargo('concurso', index)"
                  >
                    <v-icon x-small color="primary" class="mr-1">mdi-plus-circle-outline</v-icon>
                    Adicionar novo cargo
                  </div>
                  <e-label>Cargo Principal / Contratação</e-label>
                  <v-autocomplete
                    :items="cargosOptions"
                    :return-object="false"
                    v-model="profissional.cargo_concurso"
                    label="Selecione uma opção"
                    dense
                    solo
                  />
                </v-col>

                <v-col cols="12" md="4" class="pt-0 pb-0">
                  <div
                    class="text-caption primary--text font-weight-medium mt-1"
                    style="cursor: pointer"
                    @click="abrirDialogNovoCargo('exercicio', index)"
                  >
                    <v-icon x-small color="primary" class="mr-1">mdi-plus-circle-outline</v-icon>
                    Adicionar novo cargo
                  </div>
                  <e-label>Cargo em Exercício / Função</e-label>
                  <v-autocomplete
                    :items="cargosOptions"
                    :return-object="false"
                    v-model="profissional.cargo_exercicio"
                    label="Selecione uma opção"
                    dense
                    solo
                  />
                </v-col>

                <v-col cols="12" md="4" class="pt-0 pb-0" v-if="isSuperAdmin">
                  <div class="text-caption mt-1">&nbsp;</div>
                  <e-label>Lotação (Filial / Unidade)</e-label>
                  <v-autocomplete
                    :items="franquiasList"
                    :return-object="false"
                    v-model="profissional.franquias_permitidas"
                    :item-value="(franquia) => franquia.id"
                    :item-text="(franquia) => franquia.descricao"
                    :loading="carregandoFranquias"
                    label="Selecione uma opção"
                    dense
                    multiple
                    solo
                  />
                </v-col>

                <v-col cols="12" md="4" class="pt-0 pb-0">
                  <e-label>Data de Admissão</e-label>
                  <e-date-customize dense solo v-model="profissional.data_ingresso" />
                </v-col>

                <v-col cols="12" md="4" class="pt-0 pb-0">
                  <e-label>Carga Horária Semanal</e-label>
                  <v-text-field v-model="profissional.ch_total" dense solo v-mask="'#####'" />
                </v-col>

                <v-col cols="12" md="4" class="pt-0 pb-0">
                  <e-label>Turno de Trabalho</e-label>
                  <v-autocomplete
                    :items="$constants.turnosList"
                    :return-object="false"
                    v-model="profissional.turno"
                    label="Selecione uma opção"
                    dense
                    multiple
                    solo
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-card-text>
        </v-card>

        <v-card class="mb-6" outlined rounded="xl">
          <v-card-title class="px-6 pt-6 pb-2">
            <v-icon left color="primary">mdi-card-account-details</v-icon>
            <span class="text-h6 font-weight-bold primary--text">Documentação e Contato</span>
          </v-card-title>
          <v-card-text class="px-6 pb-6">
            <v-row>
              <v-col cols="12" md="3" class="pt-0 pb-0">
                <ValidationProvider name="CPF" rules="required|cpf" v-slot="{ errors }">
                  <e-label>Número do CPF <span class="red--text">*</span></e-label>
                  <v-text-field
                    v-mask="'###.###.###-##'"
                    v-model="form.pessoal.cpf"
                    :error-messages="errors"
                    dense
                    solo
                  />
                </ValidationProvider>
              </v-col>

              <v-col cols="12" md="3" class="pt-0 pb-0">
                <ValidationProvider name="RG" v-slot="{ errors }">
                  <e-label>Nº do Registro Geral (RG)</e-label>
                  <v-text-field
                    v-model="form.pessoal.rg"
                    :error-messages="errors"
                    dense
                    solo
                    v-mask="'#############'"
                  />
                </ValidationProvider>
              </v-col>

              <v-col cols="12" md="3" class="pt-0 pb-0">
                <e-label>CTPS (Carteira de Trabalho)</e-label>
                <v-text-field v-model="form.pessoal.ctps" dense solo placeholder="Nº da CTPS" />
              </v-col>

              <v-col cols="12" md="3" class="pt-0 pb-0">
                <e-label>Série CTPS</e-label>
                <v-text-field v-model="form.pessoal.serie" dense solo placeholder="Série da CTPS" />
              </v-col>

              <v-col cols="12" md="6" class="pt-0 pb-0">
                <e-label>Email</e-label>
                <v-text-field v-model="form.pessoal.email" dense solo />
              </v-col>

              <v-col cols="12" md="6" class="pt-0 pb-0">
                <e-label>Telefone</e-label>
                <v-text-field
                  v-model="form.pessoal.telefone"
                  v-mask="'(##) #####-####'"
                  dense
                  solo
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="mb-6" outlined rounded="xl">
          <v-card-title class="px-6 pt-6 pb-2">
            <v-icon left color="primary">mdi-map-marker</v-icon>
            <span class="text-h6 font-weight-bold primary--text">Endereço Residencial</span>
          </v-card-title>
          <v-card-text class="px-6 pb-6">
            <v-row>
              <v-col cols="12" md="3" class="pt-0 pb-0">
                <e-label>CEP</e-label>
                <v-text-field
                  v-mask="'#####-###'"
                  v-model="form.pessoal.cep"
                  dense
                  solo
                  :loading="buscandoCep"
                />
              </v-col>

              <v-col cols="12" md="2" class="pt-0 pb-0">
                <e-label>Estado (UF)</e-label>
                <v-autocomplete
                  :items="$constants.estadosList"
                  :return-object="false"
                  :item-text="(item) => item.codigo"
                  :item-value="(item) => item.codigo"
                  v-model="form.pessoal.uf_residencia"
                  label="Selecione"
                  dense
                  solo
                />
              </v-col>

              <v-col cols="12" md="7" class="pt-0 pb-0">
                <e-label>Cidade</e-label>
                <v-text-field v-model="form.pessoal.municipio_residencia" dense solo />
              </v-col>

              <v-col cols="12" md="4" class="pt-0 pb-0">
                <e-label>Bairro</e-label>
                <v-text-field v-model="form.pessoal.bairro_residencia" dense solo />
              </v-col>

              <v-col cols="12" md="4" class="pt-0 pb-0">
                <e-label>Rua</e-label>
                <v-text-field v-model="form.pessoal.rua_residencia" dense solo />
              </v-col>

              <v-col cols="12" md="2" class="pt-0 pb-0">
                <e-label>Número</e-label>
                <v-text-field
                  v-model="form.pessoal.numero_residencia"
                  dense
                  solo
                  v-mask="'########'"
                />
              </v-col>

              <v-col cols="12" md="2" class="pt-0 pb-0">
                <e-label>Complemento</e-label>
                <v-text-field v-model="form.pessoal.complemento_residencia" dense solo />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="mb-6" outlined rounded="xl">
          <v-card-title class="px-6 pt-6 pb-2 d-flex justify-space-between align-center">
            <div>
              <v-icon left color="primary">mdi-school</v-icon>
              <span class="text-h6 font-weight-bold primary--text">Formação Acadêmica</span>
            </div>
            <v-btn
              color="primary"
              outlined
              small
              @click="() => handleSubmit(adicionarRelacaoFormacao)"
              :disabled="submittingForm || !podeAdicionarMaisFormacoes"
              class="rounded-lg"
            >
              <v-icon left>mdi-plus</v-icon> Adicionar Formação
            </v-btn>
          </v-card-title>
          <v-card-text class="px-6 pb-6">
            <v-row class="mb-4">
              <v-col cols="12" class="pt-0 pb-0">
                <e-label>Maior Nível de Escolaridade Concluído</e-label>
                <v-autocomplete
                  :items="$constants.nivelEscolaridadeConcluidoList"
                  :return-object="false"
                  v-model="form.pessoal.nivel_escolaridade_concluido"
                  label="Selecione uma opção"
                  dense
                  solo
                />
              </v-col>
            </v-row>

            <div
              v-if="!form.formacoes || form.formacoes.length === 0"
              class="text-center py-6 grey--text font-italic"
            >
              Nenhuma formação acadêmica cadastrada. Clique no botão acima para adicionar.
            </div>

            <v-card
              v-for="(formacao, index) in form.formacoes"
              :key="'formacao-' + index"
              class="mb-4 pa-4"
              outlined
              rounded="lg"
              style="background-color: var(--surface-2); border: 1px dashed var(--border)"
            >
              <div class="d-flex justify-space-between align-center mb-3">
                <span class="text-subtitle-2 font-weight-bold primary--text"
                  >Curso #{{ index + 1 }}</span
                >
                <v-btn
                  color="error"
                  text
                  small
                  @click="() => removerRelacaoFormacao(index)"
                  :disabled="submittingForm"
                >
                  <v-icon left>mdi-delete-outline</v-icon> Remover Formação
                </v-btn>
              </div>

              <v-row>
                <v-col cols="12" md="6" class="pt-0 pb-0">
                  <e-label>Área do curso</e-label>
                  <v-autocomplete
                    :items="$constants.areaDoCursoList"
                    :return-object="false"
                    v-model="formacao.area_do_curso"
                    label="Selecione uma opção"
                    dense
                    solo
                  />
                </v-col>

                <v-col cols="12" md="6" class="pt-0 pb-0">
                  <e-label>Curso</e-label>
                  <v-text-field v-model="formacao.curso" dense solo />
                </v-col>
              </v-row>
            </v-card>
          </v-card-text>
        </v-card>

        <v-card class="mb-6" outlined rounded="xl">
          <v-card-title class="px-6 pt-6 pb-2">
            <v-icon left color="primary">mdi-file-document-multiple-outline</v-icon>
            <span class="text-h6 font-weight-bold primary--text">Anexos e Documentação</span>
          </v-card-title>
          <v-card-text class="px-6 pb-6">
            <v-row>
              <v-col cols="12">
                <div
                  class="dropboxStyles d-flex flex-column align-center justify-center py-8 rounded-xl"
                  style="
                    border: 2px dashed var(--brand);
                    background-color: var(--brand-soft);
                    transition: background-color 0.2s;
                  "
                  @click="triggerFileInput"
                >
                  <input
                    id="fileUpload"
                    class="input-file-upload"
                    type="file"
                    multiple
                    @change="selectDocumento"
                    style="display: none"
                  />
                  <v-icon color="primary" size="48" class="mb-2">mdi-cloud-upload-outline</v-icon>
                  <span class="font-weight-bold primary--text text-subtitle-1 mb-1"
                    >Upload de Documentos</span
                  >
                  <span class="text-caption grey--text"
                    >Clique para selecionar arquivos. PDF ou imagens até 2MB.</span
                  >
                </div>
              </v-col>

              <v-col
                cols="12"
                v-if="
                  (documentosArray && documentosArray.length > 0) ||
                  (pdfDocumentos && pdfDocumentos.length > 0)
                "
              >
                <div
                  class="font-weight-black mb-3 text-subtitle-2 text-uppercase grey--text text--darken-3"
                >
                  Arquivos Anexados:
                </div>

                <div
                  v-for="(file, index) in documentosArray"
                  :key="'doc-array-' + index"
                  class="d-flex align-center justify-space-between mb-2 pa-3 rounded-lg border"
                  style="
                    background-color: var(--surface-2);
                    border: 1px solid var(--border) !important;
                  "
                >
                  <div class="d-flex align-center min-width-0">
                    <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
                    <span class="text-body-2 text-truncate font-weight-bold">
                      {{ file.name || file.nomeArquivo || `Documento ${index + 1}` }}
                    </span>
                  </div>
                  <div class="d-flex">
                    <v-btn
                      icon
                      color="primary"
                      class="mx-1"
                      @click.stop="downloadDocumento(index)"
                      title="Download"
                    >
                      <v-icon>mdi-download</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      color="error"
                      class="mx-1"
                      @click.stop="apagarDocumentoForm(index)"
                      title="Excluir"
                    >
                      <v-icon>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </div>
                </div>

                <div
                  v-for="(file, index) in pdfDocumentos"
                  :key="'pdf-' + index"
                  class="d-flex align-center justify-space-between mb-2 pa-3 rounded-lg border"
                  style="
                    background-color: var(--surface-2);
                    border: 1px solid var(--border) !important;
                  "
                >
                  <div class="d-flex align-center min-width-0">
                    <v-icon color="orange" class="mr-2">mdi-file-pdf-box</v-icon>
                    <span class="text-body-2 text-truncate font-weight-bold">
                      {{ file.name || file.nomeArquivo || `PDF ${index + 1}` }}
                    </span>
                  </div>
                  <div>
                    <v-btn
                      icon
                      color="error"
                      class="mx-1"
                      @click.stop="apagarDocumento(file)"
                      title="Excluir"
                    >
                      <v-icon>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" class="d-flex flex-wrap gap-2 pt-2">
                <v-btn color="secondary" outlined @click="triggerFileInput" class="mr-2 rounded-lg">
                  <v-icon left>mdi-plus</v-icon> Incluir Documentos
                </v-btn>
                <v-btn
                  color="success"
                  outlined
                  class="rounded-lg"
                  @click="() => gerarRelatorioDossie(form.pessoal.user_id, form.pessoal.rh_id)"
                >
                  <v-icon left>mdi-file-chart-outline</v-icon> Gerar Dossiê Completo
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="mb-6" outlined rounded="xl">
          <v-card-title class="px-6 pt-6 pb-2 d-flex justify-space-between align-center">
            <div>
              <v-icon left color="primary">mdi-calendar-clock</v-icon>
              <span class="text-h6 font-weight-bold primary--text">Escalas de Trabalho</span>
            </div>
            <v-btn color="primary" small outlined @click="abrirNovaEscala" class="rounded-lg">
              <v-icon left>mdi-plus</v-icon> Adicionar Escala
            </v-btn>
          </v-card-title>
          <v-card-text class="px-6 pb-6">
            <v-data-table
              :headers="escalasHeaders"
              :items="escalas"
              :loading="loadingEscalas"
              class="elevation-0 border"
              style="border: 1px solid var(--border); border-radius: 12px; overflow: hidden"
              no-data-text="Nenhuma escala cadastrada para este colaborador."
              hide-default-footer
            >
              <template v-slot:item.ativa="{ item }">
                <v-chip :color="item.ativa ? 'green' : 'grey'" dark small>
                  {{ item.ativa ? "Ativa" : "Inativa" }}
                </v-chip>
              </template>
              <template v-slot:item.vigencia_inicio="{ item }">
                {{ formatarDataBR(item.vigencia_inicio) }}
              </template>
              <template v-slot:item.vigencia_fim="{ item }">
                {{ item.vigencia_fim ? formatarDataBR(item.vigencia_fim) : "Sem fim" }}
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn icon small class="mr-2" color="primary" @click="editarEscala(item)">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon small color="error" @click="deletarEscala(item)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <div class="d-flex justify-end mt-4 mb-6">
          <v-btn
            color="grey darken-1"
            text
            large
            class="mr-4 font-weight-bold rounded-lg"
            @click="() => $router.push({ name: 'usuarios' })"
            :disabled="submittingForm"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            large
            type="submit"
            :disabled="submittingForm || !isFormChanged"
            :loading="submittingForm"
            class="px-8 font-weight-bold rounded-lg"
            elevation="2"
          >
            <v-icon left>mdi-content-save-outline</v-icon>
            Salvar Alterações
          </v-btn>
        </div>
      </form>
    </ValidationObserver>

    <v-dialog v-model="dialogNovoCargo" max-width="420px" persistent>
      <v-card class="rounded-lg">
        <v-card-title
          class="primary white--text py-3 px-4 d-flex align-center justify-space-between mb-2"
        >
          <div class="d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-briefcase-plus-outline</v-icon>
            <span class="text-h6 font-weight-bold">Cadastrar Novo Cargo</span>
          </div>
          <v-btn icon x-small color="white" @click="dialogNovoCargo = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-2">
          <e-label>Nome do Cargo</e-label>
          <v-text-field
            v-model="novoCargo"
            dense
            solo
            autofocus
            placeholder="Ex: Técnico em Enfermagem, Agente Administrativo..."
            @keyup.enter="salvarNovoCargo"
          />
          <div class="text-caption grey--text">
            O novo cargo ficará disponível em "Cargo Principal / Contratação" e "Cargo em Exercício
            / Função" para todos os colaboradores.
          </div>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn color="secondary" text @click="dialogNovoCargo = false">Cancelar</v-btn>
          <v-btn
            color="primary"
            :loading="salvandoCargo"
            :disabled="!novoCargo.trim()"
            @click="salvarNovoCargo"
          >
            <v-icon left>mdi-check</v-icon> Salvar Cargo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogEscala" max-width="900px" persistent>
      <v-card class="rounded-lg">
        <v-card-title
          class="primary white--text py-3 px-4 d-flex align-center justify-space-between mb-2"
        >
          <div class="d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-calendar-clock</v-icon>
            <span class="text-h6 font-weight-bold">
              {{ editandoEscalaId ? "Editar Escala de Trabalho" : "Nova Escala de Trabalho" }}
            </span>
          </div>
          <v-btn icon x-small color="white" @click="dialogEscala = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-2">
          <ValidationObserver ref="obsEscala" v-slot="{ handleSubmit: handleEscalaSubmit }">
            <form @submit.prevent="handleEscalaSubmit(salvarEscala)">
              <v-container>
                <v-row v-if="!editandoEscalaId" class="mb-2">
                  <v-col cols="12">
                    <v-card outlined class="pa-3 grey lighten-5 rounded-lg border">
                      <div
                        class="subtitle-2 font-weight-bold primary--text mb-1 d-flex align-center"
                      >
                        <v-icon small color="primary" class="mr-1">mdi-content-copy</v-icon>
                        Copiar de uma Escala Existente (Opcional)
                      </div>
                      <div class="caption grey--text text--darken-1 mb-2">
                        Selecione um modelo de escala já criado no sistema para carregar
                        automaticamente os horários dos dias da semana.
                      </div>
                      <v-autocomplete
                        v-model="escalaModeloSelecionada"
                        :items="escalasModelosOptions"
                        item-text="label"
                        return-object
                        label="Selecione uma escala criada para carregar horários"
                        placeholder="Pesquise por nome da escala ou colaborador..."
                        outlined
                        dense
                        hide-details
                        clearable
                        :loading="loadingModelos"
                        @change="aplicarEscalaModelo"
                      >
                        <template v-slot:item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title class="font-weight-bold primary--text">
                              {{ item.nome }}
                            </v-list-item-title>
                            <v-list-item-subtitle class="caption grey--text">
                              Colaborador: {{ item.user ? item.user.name : "Sistema" }} | Horários:
                              {{ item.resumoHorarios }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </v-card>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="8">
                    <ValidationProvider name="Nome da Escala" rules="required" v-slot="{ errors }">
                      <e-label>Nome da Escala</e-label>
                      <v-text-field
                        v-model="escalaForm.nome"
                        :error-messages="errors"
                        dense
                        solo
                        placeholder="Ex: ESCALA CAJUTEC, ESCALA ADMINISTRATIVO..."
                      />
                    </ValidationProvider>
                  </v-col>

                  <v-col cols="12" md="4">
                    <e-label>Situação</e-label>
                    <v-switch v-model="escalaForm.ativa" label="Ativa" dense class="mt-1" />
                  </v-col>

                  <v-col cols="12" md="6">
                    <ValidationProvider
                      name="Início da Vigência"
                      rules="required"
                      v-slot="{ errors }"
                    >
                      <e-label>Início da Vigência</e-label>
                      <e-date-customize v-model="escalaForm.vigencia_inicio" />
                      <div class="red--text text-caption" v-if="errors[0]">{{ errors[0] }}</div>
                    </ValidationProvider>
                  </v-col>

                  <v-col cols="12" md="6">
                    <e-label>Fim da Vigência (Opcional)</e-label>
                    <e-date-customize v-model="escalaForm.vigencia_fim" />
                  </v-col>

                  <v-col cols="12">
                    <e-label>Observações (Opcional)</e-label>
                    <v-textarea v-model="escalaForm.observacoes" dense solo rows="2" />
                  </v-col>
                </v-row>

                <h3 class="my-4 font-weight-bold">JORNADA SEMANAL</h3>
                <v-divider class="mb-4"></v-divider>

                <v-row
                  v-for="(h, idx) in escalaForm.horarios"
                  :key="'horario-' + idx"
                  class="align-center py-2"
                  style="border-bottom: 1px solid var(--border-soft)"
                >
                  <v-col cols="12" md="2">
                    <div class="font-weight-bold">{{ h.nome }}</div>
                  </v-col>

                  <v-col cols="12" md="2">
                    <v-switch v-model="h.trabalha" label="Trabalha" dense hide-details />
                  </v-col>

                  <template v-if="h.trabalha">
                    <v-col cols="12" md="2">
                      <v-text-field
                        v-model="h.hora_entrada"
                        v-mask="'##:##'"
                        label="Entrada"
                        placeholder="08:00"
                        dense
                        solo
                        hide-details
                      />
                    </v-col>

                    <v-col cols="12" md="2">
                      <v-text-field
                        v-model="h.inicio_intervalo"
                        v-mask="'##:##'"
                        label="Início Int."
                        placeholder="12:00"
                        dense
                        solo
                        hide-details
                      />
                    </v-col>

                    <v-col cols="12" md="2">
                      <v-text-field
                        v-model="h.fim_intervalo"
                        v-mask="'##:##'"
                        label="Fim Int."
                        placeholder="13:00"
                        dense
                        solo
                        hide-details
                      />
                    </v-col>

                    <v-col cols="12" md="2">
                      <v-text-field
                        v-model="h.hora_saida"
                        v-mask="'##:##'"
                        label="Saída"
                        placeholder="17:00"
                        dense
                        solo
                        hide-details
                      />
                    </v-col>
                  </template>

                  <template v-else>
                    <v-col cols="12" md="8">
                      <span class="grey--text font-italic text-caption">Folga / Não trabalha</span>
                    </v-col>
                  </template>
                </v-row>
              </v-container>

              <v-card-actions class="mt-4">
                <v-spacer></v-spacer>
                <v-btn color="secondary" text @click="dialogEscala = false">Cancelar</v-btn>
                <v-btn color="primary" type="submit" :loading="salvandoEscala">Salvar</v-btn>
              </v-card-actions>
            </form>
          </ValidationObserver>
        </v-card-text>
      </v-card>
    </v-dialog>
  </main-template>
</template>

<script>
import { hojeBrasilia } from "@/utils/dataBrasilia";
import { mapGetters } from "vuex";
import ELabel from "../../components/ELabel.vue";

export default {
  components: { ELabel },
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
    escalasModelosOptions() {
      return (this.escalasModelos || []).map((m) => {
        const diasNomes = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
        const diasTrabalhados = (m.horarios || [])
          .filter((h) => h.trabalha)
          .map((h) => diasNomes[h.dia_semana]);
        const resumo = diasTrabalhados.length ? diasTrabalhados.join(", ") : "Sem dias de trabalho";
        const userName = m.user ? m.user.name : "Sistema";
        return {
          ...m,
          label: `${m.nome} (${userName}) — [${resumo}]`,
          resumoHorarios: resumo,
        };
      });
    },
    isAdminOrSuperAdmin() {
      return this.user && ["admin", "super-admin"].includes(this.user.roleName);
    },
    isSuperAdmin() {
      if (this.user?.roleName === "super-admin") return true;
      if (Array.isArray(this.user?.roles)) {
        return this.user.roles.some((role) => role.name === "super-admin");
      }
      return false;
    },
    fotoUrl() {
      if (this.form.pessoal.foto) {
        const apiHost = (process.env.VUE_APP_API_HOST || "").replace(/\/$/, "");
        const token = localStorage.getItem("access_token") || "";
        const tokenParam = token ? `?token=${token}` : "";
        return `${apiHost}/recursos-humanos/foto/${this.form.pessoal.foto}${tokenParam}`;
      }
      return null;
    },
    documentosArray() {
      if (typeof this.form.pessoal.documento === "string") {
        return this.form.pessoal.documento.split(",");
      }
      return this.form.pessoal.documento;
    },
    podeAdicionarMaisProfissionais() {
      if (!Array.isArray(this.form.profissionais) || this.form.profissionais.length === 0)
        return true;

      const ultimoProfissional = this.form.profissionais[this.form.profissionais.length - 1];
      if (!ultimoProfissional) return true;
      const camposObrigatorios = ["matricula"];
      return camposObrigatorios.every((campo) => ultimoProfissional[campo]);
    },
    podeAdicionarMaisFormacoes() {
      if (!Array.isArray(this.form.formacoes) || this.form.formacoes.length === 0) return true;

      const ultimoFormacao = this.form.formacoes[this.form.formacoes.length - 1];
      if (!ultimoFormacao) return true;
      const camposObrigatorios = ["area_do_curso", "curso"];
      return camposObrigatorios.every((campo) => ultimoFormacao[campo]);
    },
    isFormChanged() {
      if (!this.originalFormJson) return false;
      return JSON.stringify(this.form) !== this.originalFormJson;
    },
    cargosOptions() {
      const doBanco = Array.isArray(this.cargosDb) ? this.cargosDb.map((c) => c.nome) : [];
      return Array.from(new Set([...(this.$constants.rolesParaRhList || []), ...doBanco]));
    },
  },
  watch: {
    "form.pessoal.cep": function (newVal) {
      const cleanCep = (newVal || "").replace(/\D/g, "");
      if (cleanCep.length === 8) {
        this.buscarEnderecoPorCep(cleanCep);
      }
    },
  },
  mounted() {
    this.loadData();
  },
  data() {
    return {
      uploadingFoto: false,
      pdf: null,
      pdfDocumentos: [],
      submittingForm: false,
      buscandoCep: false,
      originalFormJson: null,
      form: {
        pessoal: {
          name: "",
          user_id: null,
          rh_id: null,
          lider_id: null,
          estado_civil_id: null,
          sexo_id: null,
          cor_ou_raca: "",
          data_nascimento: null,
          cpf: "",
          rg: "",
          ctps: "",
          serie: "",
          email: "",
          telefone: "",
          uf_residencia: "",
          cep: "",
          municipio_residencia: "",
          bairro_residencia: "",
          rua_residencia: "",
          numero_residencia: "",
          complemento_residencia: "",
          area_do_curso: "",
          curso: "",
          nivel_escolaridade_concluido: "",
          documento: [],
          foto: null,
        },
        profissionais: [],
        formacoes: [],
      },
      outrosRolesList: [],
      rolesList: [],
      lideresList: [],
      usuarios: [],
      carregandoUsuarios: false,
      usuariosLoaded: false,
      usuariosList: [],
      carregandoFranquias: false,
      franquiasList: [],
      outrosUsuariosList: [],

      cargosDb: [],
      dialogNovoCargo: false,
      novoCargo: "",
      salvandoCargo: false,
      cargoAlvo: null,
      cargoIndice: 0,

      escalas: [],
      loadingEscalas: false,
      dialogEscala: false,
      salvandoEscala: false,
      editandoEscalaId: null,
      escalasModelos: [],
      escalaModeloSelecionada: null,
      loadingModelos: false,
      escalaForm: {
        nome: "",
        ativa: true,
        vigencia_inicio: "",
        vigencia_fim: "",
        observacoes: "",
        horarios: [],
      },
      escalasHeaders: [
        { text: "Nome da Escala", value: "nome", sortable: true },
        { text: "Situação", value: "ativa", sortable: true },
        { text: "Início Vigência", value: "vigencia_inicio", sortable: true },
        { text: "Fim Vigência", value: "vigencia_fim", sortable: true },
        { text: "Ações", value: "actions", sortable: false, align: "right" },
      ],
    };
  },
  methods: {
    async buscarEnderecoPorCep(cepString) {
      this.buscandoCep = true;
      try {
        const res = await this.$services.cepService.buscarCepNoBack(cepString);
        if (res) {
          this.form.pessoal.uf_residencia = res.estado || "";
          this.form.pessoal.municipio_residencia = res.cidade || "";
          this.form.pessoal.bairro_residencia = res.bairro || "";
          this.form.pessoal.rua_residencia = res.rua || "";
        }
      } catch (error) {
        this.$toast.danger("CEP não encontrado ou erro na busca");
      } finally {
        this.buscandoCep = false;
      }
    },
    async submitForm() {
      this.submittingForm = true;
      const { usuario_id } = this.$route.params;
      this.form.pessoal.user_id = parseInt(usuario_id, 10);
      const payload = {
        user_id: parseInt(usuario_id, 10),
        ...this.form.pessoal,
        profissionais: this.form.profissionais,
        formacoes: this.form.formacoes,
      };

      try {
        await this.$services.recursosHumanosService.criarRecursosHumanos(payload);

        if (this.pdfDocumentos && this.pdfDocumentos.length > 0 && usuario_id) {
          const formData = new FormData();
          this.pdfDocumentos.forEach((file, index) => {
            formData.append(`files[${index}]`, file);
          });

          await this.$services.recursosHumanosService.uploadDocumentos(formData, usuario_id);
        }

        this.$toast.success("Usuario(a) criado(a) com sucesso");
        this.$router.push({ name: "usuarios" });
      } catch (error) {
        this.$handleError(error);
      }

      this.submittingForm = false;
    },

    async loadData() {
      try {
        this.carregandoFranquias = true;
        this.franquiasList = await this.$services.franquiasService.syncAll();
        try {
          this.lideresList = await this.$services.usuariosService.syncAll();
        } catch (e) {
          console.error("Erro ao carregar lista de usuários/líderes", e);
        }
        try {
          this.cargosDb = await this.$services.recursosHumanosService.getCargos();
        } catch (e) {
          console.error("Erro ao carregar lista de cargos", e);
        }

        const { usuario_id } = this.$route.params;
        const dadosUsuario = await this.$services.recursosHumanosService.getUsuario(usuario_id);
        const dadosRH =
          (await this.$services.recursosHumanosService.getUsuarioRecursosHumanos(usuario_id)) || {};

        this.form.pessoal = {
          name: dadosUsuario.name,
          user_id: dadosRH.user_id,
          rh_id: dadosRH.id,
          lider_id: dadosRH.lider_id ? Number(dadosRH.lider_id) : null,
          estado_civil_id: dadosRH.estado_civil_id ? Number(dadosRH.estado_civil_id) : null,
          sexo_id: dadosRH.sexo_id ? Number(dadosRH.sexo_id) : null,
          cor_ou_raca: dadosRH.cor_ou_raca,
          data_nascimento: dadosRH.data_nascimento,
          cpf: dadosRH.cpf,
          rg: dadosRH.rg,
          ctps: dadosRH.ctps || "",
          serie: dadosRH.serie || "",
          email: dadosRH.email,
          telefone: dadosRH.telefone,
          uf_residencia: dadosRH.uf_residencia,
          cep: dadosRH.cep,
          municipio_residencia: dadosRH.municipio_residencia,
          bairro_residencia: dadosRH.bairro_residencia,
          rua_residencia: dadosRH.rua_residencia,
          numero_residencia: dadosRH.numero_residencia,
          complemento_residencia: dadosRH.complemento_residencia,
          area_do_curso: dadosRH.area_do_curso,
          curso: dadosRH.curso,
          nivel_escolaridade_concluido: dadosRH.nivel_escolaridade_concluido,
          documento: dadosRH.documento,
          foto: dadosRH.foto,
        };

        this.form.profissionais = [];

        if (dadosRH.profissionais?.length > 0) {
          dadosRH.profissionais.forEach((prof) => {
            const franquiasPermitidas = prof.franquias_permitidas
              ? JSON.parse(prof.franquias_permitidas)
              : [];

            const turno = prof.turno ? JSON.parse(prof.turno) : [];

            this.form.profissionais.push({
              matricula: prof.matricula,
              regime_trabalhista: prof.regime_trabalhista,
              cargo_concurso: prof.cargo_concurso,
              cargo_exercicio: prof.cargo_exercicio,
              franquias_permitidas: franquiasPermitidas,
              data_ingresso: prof.data_ingresso,
              ch_total: prof.ch_total,
              ctps: prof.ctps || "",
              serie: prof.serie || "",
              turno,
              id: prof.id,
            });
          });
        }

        if (this.form.profissionais.length === 0) {
          this.form.profissionais.push({
            matricula: "",
            regime_trabalhista: "",
            cargo_concurso: "",
            cargo_exercicio: "",
            franquias_permitidas: [],
            data_ingresso: "",
            ch_total: "",
            ctps: "",
            serie: "",
            turno: [],
          });
        }

        this.form.formacoes = [];

        if (dadosRH.formacoes?.length > 0) {
          dadosRH.formacoes.forEach((formacao) => {
            this.form.formacoes.push({
              instituicao: formacao.instituicao,
              area_do_curso: formacao.area_do_curso,
              curso: formacao.curso,
              ano_conclusao: formacao.ano_conclusao,
              id: formacao.id,
            });
          });
        }

        if (this.form.formacoes.length === 0) {
          this.form.formacoes.push({
            instituicao: "",
            curso: "",
            nivel_escolaridade_concluido: "",
            ano_conclusao: "",
          });
        }

        await this.loadEscalas();

        this.$loaderService.close();
        this.originalFormJson = JSON.stringify(this.form);
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregandoFranquias = false;
      }
    },

    async adicionarRelacaoProfissional() {
      this.form.profissionais.push({
        matricula: "",
        regime_trabalhista: "",
        cargo_concurso: "",
        cargo_exercicio: "",
        franquias_permitidas: [],
        data_ingresso: "",
        ch_total: "",
        ctps: "",
        serie: "",
        turno: [],
      });
    },

    async removerRelacaoProfissional(index) {
      const profissional = this.form.profissionais[index];

      if (this.form.profissionais.length < 2) {
        this.$toast.danger("É necessário cadastrar pelo menos uma relação profissional");
        return;
      }

      if (profissional.id) {
        try {
          this.submittingForm = true;
          await this.$services.recursosHumanosService.removerProfissional(profissional.id);
          this.$toast.success("Relação profissional removida com sucesso");
        } catch (error) {
          this.$handleError(error);
          return;
        } finally {
          this.submittingForm = false;
        }
      }

      this.form.profissionais.splice(index, 1);
    },

    async adicionarRelacaoFormacao() {
      this.form.formacoes.push({
        instituicao: "",
        area_do_curso: "",
        curso: "",
        ano_conclusao: "",
      });
    },

    async removerRelacaoFormacao(index) {
      const formacao = this.form.formacoes[index];

      if (this.form.formacoes.length < 2) {
        this.$toast.danger("É necessário cadastrar pelo menos uma formação");
        return;
      }

      if (formacao.id) {
        try {
          this.submittingForm = true;
          await this.$services.recursosHumanosService.removerFormacao(formacao.id);
          this.$toast.success("Formação removida com sucesso");
        } catch (error) {
          this.$handleError(error);
          return;
        } finally {
          this.submittingForm = false;
        }
      }

      this.form.formacoes.splice(index, 1);
    },

    selectDocumento(event) {
      for (let i = 0; i < event.target.files.length; i++) {
        const file = event.target.files[i];
        if (file.type === "application/pdf") {
          this.pdfDocumentos.push(file);
        } else {
          this.$toast.danger(`Formato de arquivo inválido: ${file.name}`);
        }
      }
    },

    apagarDocumentoForm(index) {
      let documentos = this.form.pessoal.documento;

      if (typeof documentos === "string") {
        try {
          documentos = JSON.parse(documentos);
        } catch (e) {
          documentos = [];
        }
      }

      if (!Array.isArray(documentos)) {
        documentos = documentos ? [documentos] : [];
      }

      if (index >= 0 && index < documentos.length) {
        documentos.splice(index, 1);
      }

      this.$set(this.form.pessoal, "documento", documentos);

      if (documentos.length === 0) {
        document.querySelector("#fileUpload").value = "";
        this.pdf = null;
      }
    },

    apagarDocumento(documento) {
      const index = this.pdfDocumentos.findIndex((doc) => doc === documento);
      this.pdfDocumentos.splice(index, 1);
    },

    async downloadDocumento(file) {
      try {
        const { usuario_id } = this.$route.params;
        const data = await this.$services.recursosHumanosService.downloadDocumento(
          usuario_id,
          file
        );
        return data;
      } catch (error) {
        return this.$handleError(error);
      }
    },

    async gerarRelatorioDossie(user_id, rh_id) {
      this.$loaderService.open("Carregando dados...");
      await this.$services.recursosHumanosService.gerarRelatorioDossie(
        parseInt(user_id, 10),
        parseInt(rh_id, 10)
      );
      this.$loaderService.close();
    },

    async triggerFileInput() {
      const fileInput = document.querySelector("#fileUpload");
      fileInput.click();
    },

    async onFotoSelected(event) {
      const file = event.target.files[0];
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        this.$toast.danger("Formato de arquivo inválido. Por favor, envie uma imagem.");
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      this.uploadingFoto = true;
      try {
        const { usuario_id } = this.$route.params;
        const response = await this.$services.recursosHumanosService.uploadFoto(
          formData,
          usuario_id
        );
        this.$set(this.form.pessoal, "foto", response.foto);

        if (this.user && parseInt(usuario_id, 10) === this.user.id) {
          const updatedUser = { ...this.user };
          if (!updatedUser.recursos_humanos) {
            updatedUser.recursos_humanos = {};
          }
          updatedUser.recursos_humanos.foto = response.foto;
          this.$store.commit("Auth/setUser", updatedUser);
        }

        this.$toast.success("Foto atualizada com sucesso!");
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.uploadingFoto = false;
        document.querySelector("#fotoUpload").value = "";
      }
    },

    triggerFotoInput() {
      document.querySelector("#fotoUpload").click();
    },

    async loadEscalas() {
      const { usuario_id } = this.$route.params;
      if (!usuario_id) return;
      this.loadingEscalas = true;
      try {
        this.escalas = await this.$services.recursosHumanosService.getEscalas(usuario_id);
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.loadingEscalas = false;
      }
    },

    async carregarEscalasModelos() {
      try {
        this.loadingModelos = true;
        this.escalasModelos = await this.$services.recursosHumanosService.getEscalasModelos();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.loadingModelos = false;
      }
    },

    async abrirNovaEscala() {
      this.editandoEscalaId = null;
      this.escalaModeloSelecionada = null;
      await this.carregarEscalasModelos();
      this.escalaForm = {
        nome: "",
        ativa: true,
        vigencia_inicio: hojeBrasilia(),
        vigencia_fim: "",
        observacoes: "",
        horarios: [
          {
            dia_semana: 0,
            nome: "Domingo",
            trabalha: false,
            hora_entrada: "",
            inicio_intervalo: "",
            fim_intervalo: "",
            hora_saida: "",
            tolerancia_atraso: 10,
          },
          {
            dia_semana: 1,
            nome: "Segunda-feira",
            trabalha: true,
            hora_entrada: "08:00",
            inicio_intervalo: "12:00",
            fim_intervalo: "13:00",
            hora_saida: "17:00",
            tolerancia_atraso: 10,
          },
          {
            dia_semana: 2,
            nome: "Terça-feira",
            trabalha: true,
            hora_entrada: "08:00",
            inicio_intervalo: "12:00",
            fim_intervalo: "13:00",
            hora_saida: "17:00",
            tolerancia_atraso: 10,
          },
          {
            dia_semana: 3,
            nome: "Quarta-feira",
            trabalha: true,
            hora_entrada: "08:00",
            inicio_intervalo: "12:00",
            fim_intervalo: "13:00",
            hora_saida: "17:00",
            tolerancia_atraso: 10,
          },
          {
            dia_semana: 4,
            nome: "Quinta-feira",
            trabalha: true,
            hora_entrada: "08:00",
            inicio_intervalo: "12:00",
            fim_intervalo: "13:00",
            hora_saida: "17:00",
            tolerancia_atraso: 10,
          },
          {
            dia_semana: 5,
            nome: "Sexta-feira",
            trabalha: true,
            hora_entrada: "08:00",
            inicio_intervalo: "12:00",
            fim_intervalo: "13:00",
            hora_saida: "17:00",
            tolerancia_atraso: 10,
          },
          {
            dia_semana: 6,
            nome: "Sábado",
            trabalha: false,
            hora_entrada: "",
            inicio_intervalo: "",
            fim_intervalo: "",
            hora_saida: "",
            tolerancia_atraso: 10,
          },
        ],
      };
      this.dialogEscala = true;
    },

    aplicarEscalaModelo(modelo) {
      if (!modelo) return;
      this.escalaForm.nome = modelo.nome;
      const diasNomes = [
        "Domingo",
        "Segunda-feira",
        "Terça-feira",
        "Quarta-feira",
        "Quinta-feira",
        "Sexta-feira",
        "Sábado",
      ];
      for (let i = 0; i <= 6; i++) {
        const dbH = (modelo.horarios || []).find((h) => h.dia_semana === i);
        if (dbH) {
          this.$set(this.escalaForm.horarios, i, {
            dia_semana: i,
            nome: diasNomes[i],
            trabalha: !!dbH.trabalha,
            hora_entrada: dbH.hora_entrada ? String(dbH.hora_entrada).substring(0, 5) : "",
            inicio_intervalo: dbH.inicio_intervalo
              ? String(dbH.inicio_intervalo).substring(0, 5)
              : "",
            fim_intervalo: dbH.fim_intervalo ? String(dbH.fim_intervalo).substring(0, 5) : "",
            hora_saida: dbH.hora_saida ? String(dbH.hora_saida).substring(0, 5) : "",
            tolerancia_atraso: dbH.tolerancia_atraso || 10,
          });
        }
      }
      this.$toast.success(`Horários copiados da escala "${modelo.nome}" com sucesso!`);
    },

    async salvarEscala() {
      const { usuario_id } = this.$route.params;
      this.salvandoEscala = true;
      try {
        if (this.editandoEscalaId) {
          await this.$services.recursosHumanosService.atualizarEscala(
            usuario_id,
            this.editandoEscalaId,
            this.escalaForm
          );
          this.$toast.success("Escala atualizada com sucesso");
        } else {
          await this.$services.recursosHumanosService.salvarEscala(usuario_id, this.escalaForm);
          this.$toast.success("Escala cadastrada com sucesso");
        }
        this.dialogEscala = false;
        await this.loadEscalas();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.salvandoEscala = false;
      }
    },

    editarEscala(item) {
      this.editandoEscalaId = item.id;
      const copia = JSON.parse(JSON.stringify(item));
      const diasNomes = [
        "Domingo",
        "Segunda-feira",
        "Terça-feira",
        "Quarta-feira",
        "Quinta-feira",
        "Sexta-feira",
        "Sábado",
      ];

      const horarios = [];
      for (let i = 0; i <= 6; i++) {
        const dbH = copia.horarios.find((h) => h.dia_semana === i);
        if (dbH) {
          horarios.push({
            dia_semana: i,
            nome: diasNomes[i],
            trabalha: !!dbH.trabalha,
            hora_entrada: dbH.hora_entrada ? dbH.hora_entrada.substr(0, 5) : "",
            inicio_intervalo: dbH.inicio_intervalo ? dbH.inicio_intervalo.substr(0, 5) : "",
            fim_intervalo: dbH.fim_intervalo ? dbH.fim_intervalo.substr(0, 5) : "",
            hora_saida: dbH.hora_saida ? dbH.hora_saida.substr(0, 5) : "",
            tolerancia_atraso: dbH.tolerancia_atraso || 10,
          });
        } else {
          horarios.push({
            dia_semana: i,
            nome: diasNomes[i],
            trabalha: false,
            hora_entrada: "",
            inicio_intervalo: "",
            fim_intervalo: "",
            hora_saida: "",
            tolerancia_atraso: 10,
          });
        }
      }

      this.escalaForm = {
        nome: copia.nome,
        ativa: !!copia.ativa,
        vigencia_inicio: copia.vigencia_inicio ? copia.vigencia_inicio.substr(0, 10) : "",
        vigencia_fim: copia.vigencia_fim ? copia.vigencia_fim.substr(0, 10) : "",
        observacoes: copia.observacoes || "",
        horarios,
      };

      this.dialogEscala = true;
    },

    async deletarEscala(item) {
      const confirm = window.confirm(
        `Deseja realmente excluir a escala "${item.nome}"?\n\n` +
          "As horas do período desta escala deixam de contar no banco de horas, " +
          "inclusive as já aprovadas (créditos e débitos)."
      );
      if (!confirm) return;
      const { usuario_id } = this.$route.params;
      this.loadingEscalas = true;
      try {
        await this.$services.recursosHumanosService.deletarEscala(usuario_id, item.id);
        this.$toast.success("Escala excluída com sucesso");
        await this.loadEscalas();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.loadingEscalas = false;
      }
    },

    formatarDataBR(val) {
      if (!val) return "";
      const date = new Date(val);
      if (isNaN(date.getTime())) return val;
      const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
      return utcDate.toLocaleDateString("pt-BR");
    },

    abrirDialogNovoCargo(tipo, index) {
      this.cargoAlvo = tipo;
      this.cargoIndice = index;
      this.novoCargo = "";
      this.dialogNovoCargo = true;
    },

    async salvarNovoCargo() {
      const nomeCargo = String(this.novoCargo || "").trim();
      if (!nomeCargo) return;

      this.salvandoCargo = true;
      try {
        const cargo = await this.$services.recursosHumanosService.criarCargo(nomeCargo);

        if (
          !this.cargosDb.some(
            (c) => String(c.nome).toLowerCase() === String(cargo.nome).toLowerCase()
          )
        ) {
          this.cargosDb.push(cargo);
        }

        if (this.cargoAlvo === "concurso") {
          this.$set(this.form.profissionais[this.cargoIndice], "cargo_concurso", cargo.nome);
        } else if (this.cargoAlvo === "exercicio") {
          this.$set(this.form.profissionais[this.cargoIndice], "cargo_exercicio", cargo.nome);
        }

        this.$toast.success(`Cargo "${cargo.nome}" cadastrado com sucesso`);
        this.dialogNovoCargo = false;
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.salvandoCargo = false;
      }
    },
  },
};
</script>

<style scoped>
.dropboxStyles {
  outline: 2px dashed grey;
  background-color: var(--brand-soft);
  padding: 10px 10px;
  color: var(--text-muted);
  cursor: pointer;
}
.input-file-upload {
  opacity: 0;
  width: 100%;
  height: 60px;
  position: absolute;
  cursor: pointer;
}
</style>
